define((require) => {
    "use strict";

    const _                  = require("underscore");
    const Backbone           = require("backbone");
    const Effect             = require("shared/ui/experience/widgets/effects/Effect");
    const EffectModel        = require("shared/ui/experience/action/effects/EffectModel");
    const OpacityEffect      = require("shared/ui/experience/widgets/effects/OpacityEffect");
    const OpacityEffectModel = require("shared/ui/experience/action/effects/OpacityEffectModel");
    const ImageURLProvider   = require("shared/ui/experience/widgets/imageWidget/ImageURLProvider");

    const HOVER_DURATION = 0.3;

    /**
     * Inner class, representing the toggle image effect, extending the OpacityEffect to get the proper duration.
     *
     * @class ToggleEffect
     */
    const ToggleEffect = OpacityEffect.extend({

        duration(triggerType) {
            return triggerType === EffectModel.TRIGGER_TYPE_HOVER ? HOVER_DURATION : null;
        }

    }, {

        create(element, options) {
            return new ToggleEffect(element, new OpacityEffectModel(options));
        }

    });

    /**
     * View representing the toggle image.
     *
     * @class ToggleImageView
     */
    const ToggleImageView = Backbone.View.extend({

        tagName: "img",

        className: "toggle-effect-image",

        /**
         * The image base url of the third party image library integration
         *
         * @type {String}
         */
        _imageServingUrl: null,

        initialize() {
            const {imageWidgetView} = this.options;

            if (this.model.get("sourceType")) {
                this._imageServingUrl = this._getImageServingUrl();
            }

            this.listenTo(imageWidgetView, "updated:rendering", this.render);
        },

        visible(value) {
            const visibility = value ? "visible" : "hidden";
            this.$el.css({visibility});
        },

        _getImageServingUrl() {
            const experienceModel = this.options.imageWidgetView.viewModel.get("experience");
            const imageIntegration = experienceModel.getIntegration(this.model.get("sourceType"));
            return imageIntegration && imageIntegration.imageServingUrl;
        },

        render() {
            const {imageWidgetView} = this.options;
            const bounds = imageWidgetView.getRect();

            const provideOptions = {bounds};

            if (this.model.get("sourceType")) {
                provideOptions.imageServingUrl = this._imageServingUrl;
            }

            this.$el
                .off()
                .attr("src", ImageURLProvider.provide(this.model, provideOptions))
                .attr("alt", this.model.get("altText") || "")
                .one("error", () => {
                    if (this.model.get("sourceType") && !this._imageServingUrl) {
                        // The integration is missing, no need to retry the request without a base url
                        return;
                    }

                    // Edge case - there's a slight change the image is not "ready" yet (not generated at that specific size on the backend)
                    this.$el.attr("src", ImageURLProvider.provide(this.model));
                });

            return this;
        }

    });

    /**
     * An effect that will perform a fade (swap/toggle) transition between two images.
     *
     * @class ImageToggleEffect
     */
    return Effect.extend({

        /**
         * ToggleEffect used for tweening the toggle image.
         * @type {ToggleEffect}
         */
        _opacityEffect: null,

        _attentionTrigger: null,

        onInitialize() {
            this.toggleImageView = new ToggleImageView({
                model: this.model,
                imageWidgetView: this.view
            });

            const toggleEffectOptions = _.extend({opacity: 1.0}, this.model.pick(["duration", "postDelay", "triggerType"]));
            this._opacityEffect = ToggleEffect.create(this.toggleImageView, toggleEffectOptions);

            // Grab a reference to the image element contained inside the image widget view.
            // This will be the element that *this* effect will tween, see #config.
            this._widgetImageElement = this.view.$el.find("img").get(0);

            // Place the toggle image inside the image widget (below the image element, hence `prepend`).
            this.view.$el.prepend(this.toggleImageView.render().el);
        },

        duration(triggerType) {
            return triggerType === EffectModel.TRIGGER_TYPE_HOVER ? HOVER_DURATION : null;
        },

        play() {
            this.__super();
            // Make sure the toggle element is present when playing the effect...
            this.toggleImageView.visible(true);
            this._opacityEffect.play();
        },

        stop() {
            this._opacityEffect.stop();
            this.__super();
        },

        pause() {
            this._opacityEffect.pause();
            // Hide the clone when paused
            this.toggleImageView.visible(false);

            this.__super();
        },

        reverse() {
            this._opacityEffect.reverse();

            return this.__super();
        },

        config() {
            return {
                opacity: 0,
                ease: Effect.Easing.Linear.easeNone,
                // We'll tween opacity on the widget contained image element, and not the widget element itself.
                element: this._widgetImageElement
            };
        },

        kill() {
            this._opacityEffect.kill();
            // Clean up - remove toggle element
            this.toggleImageView.remove();

            this.__super();
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/effects/ImageToggleEffect.js